// D98.2 - Profitability tab. Internal only (never printed, D96.13).
// Props (CIP passes what it already has):
//   items       = invoice.items (read for the line money, plus quantity and unit rates for a line billed 0, D119.19)
//   creditNotes = the invoice's own credit notes (D146, item 14): nets the rows and the footer,
//                 by `invoiceProfit` in utils/clientInvoiceProfit (D119.14, the owner the header and the
//                 Margin-by-vessel report also read). Omit for "no credits exist".
//   summary     = the page's existing summary (buildClientInvoiceSummary / server); quotedTotal is read only
//                 as a zero-line invoice's typed total, and marginSanityWarning (D146: that sanity check
//                 stays gross-based, the shared owner Accounts and the PDF also read)
//   currency    = invoice currency code (blank allowed: a bare number is shown, D96.14)
//   invoiceId   = which of a credit note's linked invoices this is (D119.14: no id, no credits netted);
//                 D119.13: lets a US$ invoice also show the footer totals in TT$, at the rate pinned on it
import React, { useEffect, useState } from 'react';
import { fetchInvoicePinnedRate } from '../../lib/clientInvoiceMarginApi';
import { buildProfitability, buildTtdFigures, moneyOrBlank, pctOrBlank, type CreditNoteForProfitability } from './profitabilityRows';

export type ProfitabilitySummary = {
  quotedTotal?: number | null;
  actualCostTotal?: number | null;
  markupValueTotal?: number | null;
  profitVariance?: number | null;
  profitMarginPct?: number | null;
  markupPctVsCost?: number | null;
  marginSanityWarning?: string | null;
};

type Props = {
  items: ReadonlyArray<any> | null | undefined;
  summary: ProfitabilitySummary;
  currency?: string | null;
  creditNotes?: ReadonlyArray<CreditNoteForProfitability> | null;
  invoiceId?: string | null;
};

// D161.8 / D63: the supplier VAT USC pays is a real cost, so this cost INCLUDES it while supplier PO prices exclude it.
export const COST_INCLUDES_VAT_NOTE =
  'Includes the VAT USC paid to its suppliers (input VAT, D63). The vessel is not charged VAT. Supplier PO prices exclude VAT, so VAT-rated lines are higher here.';

const tone = (v: number | null | undefined) => (typeof v !== 'number' ? 'text-slate-500' : v < 0 ? 'text-rose-600' : 'text-slate-800');

export function ProfitabilityTab({ items, summary, currency, creditNotes, invoiceId }: Props) {
  const { rows, totals } = buildProfitability({ items, headerTotal: summary.quotedTotal }, creditNotes, invoiceId);
  // TT$ beside US$: the invoice's pinned rate (undefined = still asking, null = none pinned). A TT$ invoice is already in TT$.
  const isUsd = String(currency ?? '').trim().toUpperCase() === 'USD';
  const [rate, setRate] = useState<number | null | undefined>(undefined);
  const [rateFailed, setRateFailed] = useState(false);
  useEffect(() => {
    if (!invoiceId || !isUsd) return undefined;
    let live = true;
    setRate(undefined);
    setRateFailed(false);
    fetchInvoicePinnedRate(invoiceId)
      .then((r) => { if (live) setRate(r.usdToTtd); })
      .catch(() => { if (live) setRateFailed(true); });
    return () => { live = false; };
  }, [invoiceId, isUsd]);
  const ttd = isUsd && rate ? buildTtdFigures(totals, currency, rate) : null;
  const t = (v: number | null | undefined) => moneyOrBlank(v, 'TTD');
  const m = (v: number | null | undefined) => moneyOrBlank(v, currency);
  const figures: Array<[string, string, string?, string?]> = [
    ['Subtotal', m(totals.selling)],
    ['Actual cost', m(totals.cost), undefined, COST_INCLUDES_VAT_NOTE],
    ['Mark-up', m(totals.margin)],
    ['Mark-up % vs cost', pctOrBlank(totals.markupPct)],
    ['Margin % vs selling', pctOrBlank(totals.marginPct), summary.marginSanityWarning || undefined],
  ];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-x-8 gap-y-2 rounded-md border border-slate-200 bg-white px-4 py-3">
        {figures.map(([label, value, hint, note]) => (
          <div key={label} title={hint || note}>
            <div className="text-xs text-slate-500">{label}</div>
            <div className={`text-sm font-semibold ${hint ? 'text-rose-600' : 'text-slate-800'}`}>{value}</div>
          </div>
        ))}
      </div>
      {summary.marginSanityWarning && <div className="text-xs text-rose-700">{summary.marginSanityWarning}</div>}

      {ttd ? (
        <div className="flex flex-wrap gap-x-8 gap-y-2 rounded-md border border-slate-200 bg-white px-4 py-3" aria-label="In TT$">
          <div className="w-full text-xs text-slate-500">In TT$, at the rate pinned on this invoice (US$1 = TT${rate?.toFixed(4)})</div>
          {([
            ['Subtotal TT$', ttd.selling],
            ['Actual cost TT$', ttd.cost],
            ['Mark-up TT$', ttd.markup],
          ] as const).map(([label, value]) => (
            <div key={label}>
              <div className="text-xs text-slate-500">{label}</div>
              <div className={`text-sm font-semibold ${tone(value)}`}>{t(value)}</div>
            </div>
          ))}
        </div>
      ) : isUsd && invoiceId && (rate === null || rateFailed) ? (
        <div className="text-xs text-slate-500">
          {rateFailed ? 'The TT$ rate could not be loaded, so TT$ figures are not shown.' : 'No TT$ rate is pinned on this invoice, so TT$ figures are not shown.'}
        </div>
      ) : null}

      <div className="max-h-[60vh] overflow-auto rounded-md border border-slate-200 bg-white">
        <table className="data-table-grid w-full text-xs">
          <thead className="sticky top-0 border-b border-slate-200 bg-slate-50">
            <tr className="text-left font-semibold text-slate-600">
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Item</th>
              <th className="px-3 py-2 text-right">Selling</th>
              <th className="px-3 py-2 text-right" title={COST_INCLUDES_VAT_NOTE}>Actual cost (incl. supplier VAT)</th>
              <th className="px-3 py-2 text-right">Variance</th>
              <th className="px-3 py-2 text-right">Margin %</th>
              <th className="px-3 py-2 text-right">Mark-up %</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="px-3 py-2 text-slate-500">{r.lineNumber}</td>
                <td className="max-w-xs truncate px-3 py-2 text-slate-800">{r.label}</td>
                <td className="px-3 py-2 text-right">{m(r.selling)}</td>
                <td className="px-3 py-2 text-right">{m(r.cost)}</td>
                <td className={`px-3 py-2 text-right font-semibold ${tone(r.margin)}`}>{m(r.margin)}</td>
                <td className={`px-3 py-2 text-right ${tone(r.marginPct)}`}>{pctOrBlank(r.marginPct)}</td>
                <td className={`px-3 py-2 text-right ${tone(r.markupPct)}`}>{pctOrBlank(r.markupPct)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
            <tr>
              <td colSpan={2} className="px-3 py-2 text-slate-700">Total</td>
              <td className="px-3 py-2 text-right">{m(totals.selling)}</td>
              <td className="px-3 py-2 text-right">{m(totals.cost)}</td>
              <td className={`px-3 py-2 text-right ${tone(totals.margin)}`}>{m(totals.margin)}</td>
              <td className="px-3 py-2 text-right">{pctOrBlank(totals.marginPct)}</td>
              <td className="px-3 py-2 text-right">{pctOrBlank(totals.markupPct)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
